/**
 * usePlanChange — execute an in-app subscription plan change.
 *
 * in-app-checkout Phase 3 Session 3.5.
 *
 * Calls `PUT /api/billing/subscription` with `{ tier_id }` which
 * performs `subscriptions.update({ items, proration_behavior: 'always_invoice' })`
 * on the Stripe side — BOTH upgrade and downgrade are IMMEDIATE under this policy
 * (mirrors the existing Customer Portal config; see design.md § API Contracts).
 *
 * On success:
 *   - Invalidates `queryKeys.billing.usage(userId)` so BillingHub refreshes with
 *     the new tier, grant, and period figures immediately.
 *   - `retry: 0` — subscription mutations must not auto-retry (idempotency risk).
 *
 * Error handling:
 *   - The mutation throws `ApiError` on HTTP error (invalid tier, not subscribed,
 *     already on that tier, etc.). The component maps to user-facing copy.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type {
  PlanChangeRequest,
  PlanChangeResponse,
} from '@arcanum-hq/shared-types';

interface PlanChangeEnvelope {
  data: PlanChangeResponse;
}

export function usePlanChange(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<PlanChangeResponse, Error, PlanChangeRequest>({
    mutationFn: async ({ tier_id }) => {
      const envelope = await api.put<PlanChangeEnvelope>(
        '/api/billing/subscription',
        { tier_id } satisfies PlanChangeRequest
      );
      return envelope.data;
    },
    // Never auto-retry subscription mutations — idempotency risk.
    retry: 0,
    onSuccess: () => {
      // Invalidate usage so BillingHub shows the new tier and grant immediately.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.billing.usage(userId),
      });
      // Invalidate invoices (a new prorated invoice will appear).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.billing.invoices(userId),
      });
    },
  });
}
